/**
 * One project. Managers (their own) and admins manage members, templates and
 * labels here; executives see which templates they can open. Every rule is
 * enforced by the Worker; hiding controls here is only for clarity.
 */
import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { api } from '../api/client';
import type { ExecutiveEntry, Label, ProjectDetail, TemplateSummary } from '../api/types';
import { useCan } from '../auth/session';
import { Badge, Button, ErrorBox, Field, formatDateTime, Modal, Select, Spinner, TableCard, TextInput } from '../components/ui';
import { ProjectDocuments, StartDocument } from '../fill/DocumentLists';
import { KIND_HINT, KIND_LABEL } from './ProjectsPage';
import { ShareLinksSection } from '../share/ShareLinksSection';
import { listReviews, myReviewsKey } from '../reviews/api';
import { MyReview } from '../reviews/MyReview';
import { REVIEW_TAG } from '../reviews/ReviewTag';

const SUBTYPE_LABEL = { view: 'executive-view', editor: 'executive-editor' } as const;
const TAG_CLASS = { amber: 'bg-amber-100 text-amber-900', blue: 'bg-blue-100 text-blue-800', green: 'bg-green-100 text-green-800' } as const;

export function ProjectPage({ id }: { id: string }) {
  const detail = useQuery({ queryKey: ['project', id], queryFn: () => api<ProjectDetail>('GET', `/api/projects/${id}`) });
  if (detail.isPending) return <Spinner label="Opening project" />;
  if (detail.isError) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <ErrorBox error={detail.error} />
        <p className="mt-3"><Link to="/projects" className="text-blue-800 underline">Back to projects</Link></p>
      </div>
    );
  }
  return <ProjectView data={detail.data} />;
}

function useProjectMutation<T>(projectId: string, fn: (arg: T) => Promise<unknown>, after?: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project', projectId] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      after?.();
    },
  });
}

function ProjectView({ data }: { data: ProjectDetail }) {
  const { project } = data;
  const manage = project.canManage;
  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6 p-6">
      <p className="text-sm"><Link to="/projects" className="text-blue-800 underline">← Projects</Link></p>
      <Header data={data} />
      <TemplatesSection data={data} />
      <ProjectDocuments data={data} />
      {project.kind === 'work' && manage && <MembersSection data={data} />}
      {project.kind === 'share' && manage && <ShareLinksSection data={data} />}
      {manage && <DangerZone data={data} />}
    </div>
  );
}

/* --------------------------------------------------------- header */

function Header({ data }: { data: ProjectDetail }) {
  const { project } = data;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(project.name);
  const rename = useProjectMutation(project.id, (n: string) => api('PATCH', `/api/projects/${project.id}`, { body: { name: n } }), () => setEditing(false));
  const labels = useQuery({ queryKey: ['labels'], queryFn: () => api<{ labels: Label[] }>('GET', '/api/labels'), enabled: project.canManage });
  const setLabels = useProjectMutation(project.id, (ids: string[]) => api('PUT', `/api/projects/${project.id}/labels`, { body: { labelIds: ids } }));
  const current = project.labels.map(l => l.id);
  const canAudit = useCan('audit.read');

  return (
    <header className="flex flex-col gap-2">
      {editing ? (
        <form className="flex flex-wrap items-end gap-2" onSubmit={e => { e.preventDefault(); if (name.trim()) rename.mutate(name.trim()); }}>
          <Field label="Project name">{fid => <TextInput id={fid} value={name} onChange={e => setName(e.target.value)} maxLength={120} autoFocus required />}</Field>
          <Button tone="primary" type="submit" disabled={!name.trim() || rename.isPending}>Save</Button>
          <Button onClick={() => { setEditing(false); setName(project.name); }}>Cancel</Button>
        </form>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-semibold">{project.name}</h1>
          {project.canManage && <Button tone="ghost" onClick={() => setEditing(true)}>Rename</Button>}
          {canAudit && <a href={`/admin/activity?projectId=${encodeURIComponent(project.id)}`} className="text-sm text-blue-800 underline">Activity in this project</a>}
        </div>
      )}
      <ErrorBox error={rename.error} />
      <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
        <Badge tone={project.kind === 'work' ? 'blue' : 'amber'}>{KIND_LABEL[project.kind]}</Badge>
        <span>{KIND_HINT[project.kind]}</span>
      </p>
      <p className="text-xs text-slate-500">
        Created by {project.createdBy.displayName} · updated {formatDateTime(project.updatedAt)}
      </p>
      <div className="flex flex-wrap items-center gap-1 text-xs" aria-label="Labels">
        {project.labels.map(l => (
          <span key={l.id} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5">
            {l.name}
            {project.canManage && (
              <button type="button" aria-label={`Remove label ${l.name}`} onClick={() => setLabels.mutate(current.filter(c => c !== l.id))}>✕</button>
            )}
          </span>
        ))}
        {project.canManage && (
          <select aria-label="Add a label" className="rounded border border-slate-300 px-1 py-0.5 text-xs" value=""
            onChange={e => { if (e.target.value) setLabels.mutate([...current, e.target.value]); }}>
            <option value="">+ Label</option>
            {labels.data?.labels.filter(l => !current.includes(l.id)).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        )}
      </div>
      <ErrorBox error={setLabels.error} />
    </header>
  );
}

/* ------------------------------------------------------ templates */

function TemplatesSection({ data }: { data: ProjectDetail }) {
  const { project, templates } = data;
  const manage = project.canManage;
  const canEditTemplates = useCan('template.customize');
  const canFill = useCan('document.fill') && project.kind === 'work';
  // The executive's own review requests here (item 5): a tag in the template row that opens his request.
  const canAsk = useCan('review.ask') && project.kind === 'work' && !manage;
  const myReviews = useQuery({ queryKey: myReviewsKey(project.id), queryFn: () => listReviews({ projectId: project.id }), enabled: canAsk });
  const [shownReview, setShownReview] = useState<string | null>(null);
  const [pick, setPick] = useState('');
  const available = useQuery({
    queryKey: ['templates'],
    queryFn: () => api<{ templates: TemplateSummary[] }>('GET', '/api/templates'),
    enabled: manage,
  });
  const add = useProjectMutation(project.id, (code: string) => api('POST', `/api/projects/${project.id}/templates`, { body: { templateCode: code } }), () => setPick(''));
  const remove = useProjectMutation(project.id, (code: string) => api('DELETE', `/api/projects/${project.id}/templates/${encodeURIComponent(code)}`));
  const inProject = new Set(templates.map(t => t.templateCode));
  const choices = (available.data?.templates ?? []).filter(t => t.currentVersion !== null && t.status !== 'archived' && !inProject.has(t.templateCode));
  // "My custom templates" first, then masters grouped by document type, so the
  // list stays usable as the catalogue grows.
  const mine = choices.filter(t => t.kind === 'custom');
  const masters = choices.filter(t => t.kind === 'master');
  const groups = [...new Set(masters.map(t => t.natureOfDocument))].sort();

  return (
    <section aria-labelledby="tpl-heading" className="flex flex-col gap-2">
      <h2 id="tpl-heading" className="text-lg font-semibold">Templates ({templates.length})</h2>
      {templates.length ? (
        <TableCard>
          <caption className="sr-only">Templates in this project</caption>
          <thead>
            <tr className="border-b border-slate-300 text-left">
              <th scope="col" className="py-2">Title</th><th scope="col">Code</th><th scope="col">Nature</th><th scope="col">Version</th>
              {(manage || canFill) && <th scope="col"><span className="sr-only">Actions</span></th>}
            </tr>
          </thead>
          <tbody>
            {templates.map(t => {
              const review = myReviews.data?.find(r => r.templateCode === t.templateCode) ?? null;
              const shown = review !== null && shownReview === review.id;
              return (<Fragment key={t.templateCode}>
              <tr className="border-b border-slate-200">
                <td className="py-2 font-medium">
                  {t.title}
                  {t.kind === 'custom' && t.baseTitle && <span className="block text-xs font-normal text-slate-500">custom, made from "{t.baseTitle}"</span>}
                  {review && (
                    <button type="button" aria-expanded={shown} onClick={() => setShownReview(shown ? null : review.id)}
                      title={shown ? 'Hide your question' : 'Show your question and the answer'}
                      className={`ml-2 rounded px-1.5 py-0.5 text-xs font-medium underline focus-visible:ring-2 focus-visible:ring-blue-600 ${TAG_CLASS[REVIEW_TAG[review.state].tone]}`}>
                      {REVIEW_TAG[review.state].text}
                    </button>
                  )}
                </td>
                <td><code className="text-xs">{t.templateCode}</code></td>
                <td>{t.natureOfDocument}</td>
                <td>
                  {t.status === 'archived' ? <Badge tone="red">archived</Badge>
                    : t.currentVersion ? <Badge tone="green">v{t.currentVersion} (latest)</Badge>
                      : <Badge tone="amber">unpublished</Badge>}
                </td>
                {manage && (
                  <td className="whitespace-nowrap text-right">
                    {t.kind === 'custom' && canEditTemplates && (
                      <Link to="/templates/$id" params={{ id: t.id }} className="px-2 text-sm text-blue-800 underline">Edit</Link>
                    )}
                    <Button tone="ghost" disabled={remove.isPending} onClick={() => remove.mutate(t.templateCode)} aria-label={`Remove ${t.title} from the project`}>Remove</Button>
                  </td>
                )}
                {!manage && canFill && (
                  <td className="py-1 text-right"><StartDocument projectId={project.id} templateCode={t.templateCode} title={t.title} /></td>
                )}
              </tr>
              {shown && <tr><td colSpan={5} className="pb-3"><MyReview review={review} /></td></tr>}
              </Fragment>);
            })}
          </tbody>
        </TableCard>
      ) : (
        <p className="text-sm text-slate-500">{manage ? 'No templates yet. Add one below.' : 'No templates in this project yet.'}</p>
      )}
      {canFill && templates.length > 0 && (
        <p className="text-xs text-slate-500">Press <b>Start a document</b> to fill a template. Your documents are listed below.</p>
      )}
      <ErrorBox error={remove.error} />
      {manage && (
        <form className="flex flex-wrap items-end gap-2" onSubmit={e => { e.preventDefault(); if (pick) add.mutate(pick); }}>
          <Field label="Add a published template">{fid => (
            <Select id={fid} value={pick} onChange={e => setPick(e.target.value)}>
              <option value="">{available.isPending ? 'Loading…' : choices.length ? 'Choose a template' : 'No other published templates'}</option>
              {mine.length > 0 && (
                <optgroup label="My custom templates">
                  {mine.map(t => <option key={t.templateCode} value={t.templateCode}>{t.title} · {t.templateCode} · v{t.currentVersion}</option>)}
                </optgroup>
              )}
              {groups.map(g => (
                <optgroup key={g} label={`Master: ${g}`}>
                  {masters.filter(t => t.natureOfDocument === g).map(t => <option key={t.templateCode} value={t.templateCode}>{t.title} · {t.templateCode} · v{t.currentVersion}</option>)}
                </optgroup>
              ))}
            </Select>
          )}</Field>
          <Button type="submit" disabled={!pick || add.isPending}>Add template</Button>
        </form>
      )}
      {manage && canEditTemplates && (
        <p className="text-xs text-slate-500">
          Need different wording or fixed values for this client? Go to <Link to="/templates" className="text-blue-800 underline">Templates</Link>,
          press <b>Make my own copy</b> on a master template, give it a name, then add it here.
        </p>
      )}
      <ErrorBox error={add.error} />
    </section>
  );
}

/* -------------------------------------------------------- members */

function MembersSection({ data }: { data: ProjectDetail }) {
  const { project, members } = data;
  const [pick, setPick] = useState('');
  const [typed, setTyped] = useState('');
  const directory = useQuery({ queryKey: ['executives'], queryFn: () => api<{ executives: ExecutiveEntry[] }>('GET', '/api/people/executives') });
  const add = useProjectMutation(project.id, (code: string) => api('POST', `/api/projects/${project.id}/members`, { body: { userCode: code } }), () => { setPick(''); setTyped(''); });
  const remove = useProjectMutation(project.id, (code: string) => api('DELETE', `/api/projects/${project.id}/members/${encodeURIComponent(code)}`));
  const inProject = new Set(members.map(m => m.userCode));
  const choices = (directory.data?.executives ?? []).filter(e => !inProject.has(e.userCode));

  return (
    <section aria-labelledby="members-heading" className="flex flex-col gap-2">
      <h2 id="members-heading" className="text-lg font-semibold">Executives ({members.length})</h2>
      <p className="text-xs text-slate-500">Everyone listed here can open every template above. Removing someone takes effect on their next click.</p>
      {members.length ? (
        <TableCard>
          <caption className="sr-only">Executives in this project</caption>
          <thead>
            <tr className="border-b border-slate-300 text-left">
              <th scope="col" className="py-2">Name</th><th scope="col">Code</th><th scope="col">Access</th><th scope="col">Added</th>
              <th scope="col"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {members.map(m => (
              <tr key={m.userCode} className="border-b border-slate-200">
                <td className="py-2">{m.displayName} {m.status === 'disabled' && <Badge tone="red">disabled</Badge>}</td>
                <td><code className="text-xs">{m.userCode}</code></td>
                <td>{m.execSubtype ? <Badge tone={m.execSubtype === 'editor' ? 'blue' : 'slate'}>{SUBTYPE_LABEL[m.execSubtype]}</Badge> : m.role}</td>
                <td className="text-xs text-slate-600">{formatDateTime(m.addedAt)}</td>
                <td className="text-right">
                  <Button tone="ghost" disabled={remove.isPending} onClick={() => remove.mutate(m.userCode)} aria-label={`Remove ${m.displayName} from the project`}>Remove</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </TableCard>
      ) : <p className="text-sm text-slate-500">No executives yet.</p>}
      <ErrorBox error={remove.error} />

      <div className="flex flex-wrap items-end gap-4">
        <form className="flex items-end gap-2" onSubmit={e => { e.preventDefault(); if (pick) add.mutate(pick); }}>
          <Field label="Add an executive">{fid => (
            <Select id={fid} value={pick} onChange={e => setPick(e.target.value)}>
              <option value="">{directory.isPending ? 'Loading…' : choices.length ? 'Choose a person' : 'Everyone is already added'}</option>
              {choices.map(x => <option key={x.userCode} value={x.userCode}>{x.displayName} · {x.userCode} · {SUBTYPE_LABEL[x.execSubtype]}</option>)}
            </Select>
          )}</Field>
          <Button type="submit" disabled={!pick || add.isPending}>Add</Button>
        </form>
        <form className="flex items-end gap-2" onSubmit={e => { e.preventDefault(); if (typed.trim()) add.mutate(typed.trim()); }}>
          <Field label="Or enter a user code">{fid => <TextInput id={fid} value={typed} onChange={e => setTyped(e.target.value)} placeholder="U-7K3M9QXP" maxLength={20} className="w-36 font-mono" />}</Field>
          <Button type="submit" disabled={!typed.trim() || add.isPending}>Add</Button>
        </form>
      </div>
      <ErrorBox error={add.error} />
    </section>
  );
}

/* ---------------------------------------------------------- delete */

function DangerZone({ data }: { data: ProjectDetail }) {
  const { project } = data;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canCreate = useCan('project.create');
  const [confirming, setConfirming] = useState(false);
  const del = useMutation({
    mutationFn: () => api('DELETE', `/api/projects/${project.id}`),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['project', project.id] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void navigate({ to: '/projects' });
    },
  });
  if (!canCreate) return null;
  const blocked = project.documentCount > 0;
  return (
    <section aria-labelledby="danger-heading" className="flex flex-col gap-2 border-t border-slate-200 pt-4">
      <h2 id="danger-heading" className="text-sm font-semibold text-slate-700">Delete project</h2>
      <p className="text-xs text-slate-500">
        {blocked ? `This project has ${project.documentCount} document(s), so it cannot be deleted.` : `Removes the project, its ${project.kind === 'share' ? 'share links (they stop working)' : 'member list'} and its template list. Templates themselves are not affected.`}
      </p>
      <div><Button tone="danger" disabled={blocked} onClick={() => setConfirming(true)}>Delete project…</Button></div>
      <Modal open={confirming} title={`Delete "${project.name}"?`} onClose={() => setConfirming(false)}>
        <div className="flex flex-col gap-3">
          <p className="text-sm">
            {project.kind === 'work' ? `${project.memberCount} executive(s) lose access to its templates straight away.` : 'Its templates stay available elsewhere.'} This cannot be undone.
          </p>
          <ErrorBox error={del.error} />
          <div className="flex justify-end gap-2">
            <Button onClick={() => setConfirming(false)}>Cancel</Button>
            <Button tone="danger" disabled={del.isPending} onClick={() => del.mutate()}>Delete</Button>
          </div>
        </div>
      </Modal>
    </section>
  );
}
